import type { EntityResponse } from '@/backend/types'
import { serializeEntity } from '@/modules/Project/serializer'
import type { Entity } from '@/modules/Project/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * The global store that holds most up to date records for all entities
 * It may or may not get cleaned up as we switch workspaces/projects.
 *
 * Generally, other stores will hold only entity ids and will use entitiesById/getEntity
 * to get the full entity.
 *
 * Calling `useEntityClient().fetchX` will update records in  this store.
 */

export const useEntityStore = defineStore('globalEntities', () => {
  const entitiesById = ref<Record<string, Entity | undefined>>({})

  const pushEntityJSON = (json: EntityResponse, workspaceId: string): Entity => {
    const entity = serializeEntity(json, workspaceId)
    entitiesById.value[json.id] = entity

    return entity
  }

  const pushEntity = (entity: Entity): void => {
    entitiesById.value[entity.id] = entity
  }

  const getFullEntity = (entityId: string): Entity | undefined => entitiesById.value[entityId]
  const getPartialEntity = (entityId: string): Entity | undefined =>
    partialEntitiesById.value[entityId]
  const getFullOrPartialEntity = (entityId: string): Entity | undefined =>
    getFullEntity(entityId) || getPartialEntity(entityId)

  /**
   * Lookup map for entities received from backend when fetching a list of entities.
   * Misses details such as grounding information,
   * so cannot be used interchangeably with `entitiesById` in some scenarios.
   */
  const partialEntitiesById = ref<Record<string, Entity | undefined>>({})

  const pushPartialEntityJSON = (json: EntityResponse, workspaceId: string): Entity => {
    const entity = serializeEntity(json, workspaceId)
    partialEntitiesById.value[json.id] = entity
    return entity
  }

  const pushPartialEntity = (entity: Entity) => {
    partialEntitiesById.value[entity.id] = entity
  }

  const removeEntities = (entityIds: string[]): void => {
    entityIds.forEach((entityId) => {
      entitiesById.value[entityId] = undefined
      partialEntitiesById.value[entityId] = undefined
    })
  }

  function findEntitiesForAgent(agentId: string): Entity[] {
    return Object.values(entitiesById.value).filter(
      (entity): entity is Entity => !!entity && entity.projectId === agentId,
    )
  }

  return {
    entitiesById,
    partialEntitiesById,
    findEntitiesForAgent,
    getFullEntity,
    getPartialEntity,
    getFullOrPartialEntity,
    pushEntity,
    pushEntityJSON,
    pushPartialEntity,
    pushPartialEntityJSON,
    removeEntities,
  }
})
